import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { qk } from '../lib/queryClient.ts';
import { toast } from '../lib/toast.tsx';
import { share } from '../lib/share.ts';
import { navigate } from '../lib/router.ts';
import { Icon } from '../lib/icons.tsx';
import type { ServiceRequestDTO } from '@newflix/shared';

function extUrl(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function hostOf(url: string): string {
  try {
    return new URL(extUrl(url)).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function Avatar({ src, name }: { src: string | null; name: string }) {
  if (src) {
    return <img src={src} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />;
  }
  return (
    <span
      style={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        background: 'var(--accent-soft)',
        color: 'var(--accent)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {(name || '?').slice(0, 1).toUpperCase()}
    </span>
  );
}

export function RequestsPage() {
  const qc = useQueryClient();
  const [sort, setSort] = useState<'top' | 'new'>('top');
  const meQ = useQuery({ queryKey: qk.auth.me(), queryFn: () => api.me(), retry: false });
  const isLoggedIn = !!meQ.data?.user;
  const listQ = useQuery({
    queryKey: qk.requests.list(sort),
    queryFn: () => api.listRequests(sort, 1, 60),
  });
  const items = listQ.data?.items ?? [];

  const [showSubmit, setShowSubmit] = useState(false);
  const [detail, setDetail] = useState<ServiceRequestDTO | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['requests'] });

  const requireLogin = (): boolean => {
    if (isLoggedIn) return true;
    toast.info('로그인이 필요해요');
    navigate('/me');
    return false;
  };

  const onLike = async (r: ServiceRequestDTO) => {
    if (!requireLogin()) return;
    try {
      if (r.likedByMe) await api.unlikeRequest(r.id);
      else await api.likeRequest(r.id);
      refresh();
    } catch {
      toast.error('처리 실패');
    }
  };

  return (
    <main className="nf-page" style={{ paddingTop: 80 }}>
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '24px var(--gutter) 100px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <h1 style={{ fontSize: 30, fontWeight: 900, margin: '0 0 6px' }}>서비스 요청</h1>
            <p style={{ fontSize: 14, color: 'var(--fg-muted)', margin: 0 }}>
              보고 싶은 서비스를 요청하고, 좋아요로 우선순위를 올려보세요. 관리자와 코멘트로 소통할 수 있어요.
            </p>
          </div>
          <button
            className="nf-btn ghost"
            style={{ height: 38, padding: '0 14px' }}
            onClick={() => share({ title: 'newflix 서비스 요청', url: '/requests' })}
            title="이 페이지 공유"
          >
            <Icon.share /> 공유
          </button>
          <button
            className="nf-btn accent"
            style={{ height: 38, padding: '0 16px' }}
            onClick={() => (requireLogin() ? setShowSubmit(true) : undefined)}
          >
            <Icon.plus /> 요청하기
          </button>
        </div>

        <div style={{ display: 'flex', gap: 6, margin: '20px 0 16px' }}>
          {(['top', 'new'] as const).map((s) => (
            <button
              key={s}
              className={`nf-btn ${sort === s ? 'accent' : 'ghost'}`}
              style={{ height: 30, padding: '0 12px', fontSize: 12 }}
              onClick={() => setSort(s)}
            >
              {s === 'top' ? '인기' : '최신'}
            </button>
          ))}
        </div>

        {listQ.isPending ? (
          <div style={{ color: 'var(--fg-muted)', fontSize: 13, padding: '40px 0' }}>불러오는 중…</div>
        ) : items.length === 0 ? (
          <div style={{ color: 'var(--fg-muted)', fontSize: 14, padding: '40px 0', textAlign: 'center' }}>
            아직 요청이 없어요. 첫 요청을 남겨보세요.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((r) => (
              <RequestCard key={r.id} r={r} onLike={() => onLike(r)} onOpen={() => setDetail(r)} />
            ))}
          </div>
        )}
      </div>

      {showSubmit && <SubmitModal onClose={() => setShowSubmit(false)} onDone={refresh} />}
      {detail && (
        <DetailModal
          request={detail}
          isLoggedIn={isLoggedIn}
          onClose={() => setDetail(null)}
          onRequireLogin={requireLogin}
        />
      )}
    </main>
  );
}

function RequestCard({
  r,
  onLike,
  onOpen,
}: {
  r: ServiceRequestDTO;
  onLike: () => void;
  onOpen: () => void;
}) {
  return (
    <div
      onClick={onOpen}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        padding: 0,
        border: '1px solid var(--line)',
        borderRadius: 12,
        background: 'var(--bg-elev)',
        cursor: 'pointer',
        overflow: 'hidden',
      }}
    >
      {/* 1단: 연결된 서비스 (있을 때만, 상단 강조 배너) */}
      {r.linkedService && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 16px',
            background: 'rgba(0,200,150,.08)',
            borderBottom: '1px solid var(--line)',
          }}
        >
          {r.linkedService.faviconUrl ? (
            <img
              src={r.linkedService.faviconUrl}
              alt=""
              style={{ width: 22, height: 22, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 5,
                background: 'var(--accent-soft)',
                color: 'var(--accent)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {r.linkedService.name.slice(0, 1).toUpperCase()}
            </span>
          )}
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--positive)',
              letterSpacing: '.04em',
              textTransform: 'uppercase',
            }}
          >
            등록됨
          </span>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--fg)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: '1 1 auto',
            }}
          >
            {r.linkedService.name}
          </span>
          <span style={{ fontSize: 12, color: 'var(--accent)', flexShrink: 0 }}>→</span>
        </div>
      )}

      {/* 2단: 업보트 + 서비스명(요청명 + url + 코멘트수) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px' }}>
        <button
          className="nf-btn ghost"
          onClick={(e) => {
            e.stopPropagation();
            onLike();
          }}
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            gap: 2,
            padding: 0,
            flexShrink: 0,
            borderColor: r.likedByMe ? 'var(--accent)' : 'var(--line)',
            color: r.likedByMe ? 'var(--accent)' : 'var(--fg-soft)',
          }}
          title="좋아요"
        >
          <span style={{ fontSize: 13 }}>▲</span>
          <span style={{ fontSize: 12, fontWeight: 700 }}>{r.likeCount}</span>
        </button>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: 'var(--fg)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {r.name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg-dim)', marginTop: 2 }}>{hostOf(r.url)}</div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            color: 'var(--fg-dim)',
            fontSize: 12,
            flexShrink: 0,
          }}
          title="코멘트"
        >
          <Icon.bookmark />
          {r.commentCount}
        </div>
      </div>

      {/* 3단: 사용자명 (요청자) */}
      {r.requester && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',
            borderTop: '1px solid var(--line-soft)',
            background: 'rgba(255,255,255,.02)',
            fontSize: 12,
            color: 'var(--fg-muted)',
          }}
        >
          <Avatar src={r.requester.avatar} name={r.requester.displayName} />
          <span style={{ color: 'var(--fg-soft)', fontWeight: 600 }}>
            {r.requester.displayName}
          </span>
          <span style={{ color: 'var(--fg-dim)' }}>@{r.requester.username}</span>
        </div>
      )}
    </div>
  );
}

function ModalShell({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(0,0,0,.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)',
          maxHeight: '85vh',
          overflowY: 'auto',
          background: '#16161e',
          border: '1px solid var(--line)',
          borderRadius: 16,
          padding: 24,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function SubmitModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || !url.trim()) {
      toast.error('이름과 URL을 입력하세요');
      return;
    }
    setBusy(true);
    try {
      const res = await api.submitRequest(name.trim(), url.trim());
      if (res.kind === 'exists') {
        toast.info('이미 등록된 서비스예요');
        if (res.service) navigate('/s/' + res.service.slug);
      } else if (res.kind === 'merged') {
        toast.success('이미 있는 요청이라 좋아요를 더했어요');
      } else {
        toast.success('요청이 등록됐어요. 검토 후 반영됩니다');
      }
      onClose();
      onDone();
    } catch (e) {
      toast.error((e as Error).message ?? '요청 실패');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    background: 'var(--bg)',
    border: '1px solid var(--line)',
    borderRadius: 8,
    padding: '10px 12px',
    color: 'var(--fg)',
    fontSize: 14,
    marginTop: 6,
  };

  return (
    <ModalShell onClose={onClose}>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 4px' }}>서비스 요청하기</h2>
      <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: '0 0 16px' }}>
        서비스 이름과 URL만 알려주세요. 같은 도메인은 자동으로 합쳐집니다.
      </p>
      <label style={{ fontSize: 12, color: 'var(--fg-soft)', fontWeight: 600 }}>
        이름
        <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="예: Linear" maxLength={200} />
      </label>
      <label style={{ fontSize: 12, color: 'var(--fg-soft)', fontWeight: 600, display: 'block', marginTop: 14 }}>
        URL
        <input style={inputStyle} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="예: linear.app" maxLength={2000} />
      </label>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
        <button className="nf-btn ghost" style={{ height: 36, padding: '0 14px' }} onClick={onClose} disabled={busy}>
          취소
        </button>
        <button className="nf-btn accent" style={{ height: 36, padding: '0 16px' }} onClick={submit} disabled={busy}>
          {busy ? '제출 중…' : '제출'}
        </button>
      </div>
    </ModalShell>
  );
}

function DetailModal({
  request,
  isLoggedIn,
  onClose,
  onRequireLogin,
}: {
  request: ServiceRequestDTO;
  isLoggedIn: boolean;
  onClose: () => void;
  onRequireLogin: () => boolean;
}) {
  const qc = useQueryClient();
  const commentsQ = useQuery({
    queryKey: qk.requests.comments(request.id),
    queryFn: () => api.listRequestComments(request.id),
  });
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const comments = commentsQ.data?.items ?? [];

  const post = async () => {
    if (!onRequireLogin()) return;
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.addRequestComment(request.id, body.trim());
      setBody('');
      qc.invalidateQueries({ queryKey: qk.requests.comments(request.id) });
      qc.invalidateQueries({ queryKey: ['requests', 'list'] });
    } catch (e) {
      toast.error((e as Error).message ?? '코멘트 실패');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h2 style={{ fontSize: 19, fontWeight: 800, margin: 0, flex: '1 1 auto' }}>{request.name}</h2>
      </div>
      {request.linkedService && (
        <button
          onClick={() => navigate('/s/' + request.linkedService!.slug)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            margin: '14px 0',
            padding: '12px 14px',
            border: '1px solid var(--line)',
            borderRadius: 12,
            background: 'var(--bg-elev)',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          {request.linkedService.faviconUrl ? (
            <img
              src={request.linkedService.faviconUrl}
              alt=""
              style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                background: 'var(--accent-soft)',
                color: 'var(--accent)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {request.linkedService.name.slice(0, 1).toUpperCase()}
            </span>
          )}
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--positive)', fontWeight: 700 }}>등록된 서비스</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--fg)' }}>{request.linkedService.name}</div>
          </div>
          <span style={{ fontSize: 13, color: 'var(--accent)', flexShrink: 0 }}>서비스 보기 →</span>
        </button>
      )}
      <a
        href={extUrl(request.url)}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontSize: 13, color: 'var(--accent)', wordBreak: 'break-all' }}
      >
        {request.url}
      </a>
      <div style={{ fontSize: 12, color: 'var(--fg-dim)', margin: '6px 0 18px' }}>
        ▲ {request.likeCount} · 코멘트 {comments.length}
      </div>

      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {commentsQ.isPending ? (
          <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>불러오는 중…</div>
        ) : comments.length === 0 ? (
          <div style={{ color: 'var(--fg-dim)', fontSize: 13 }}>아직 코멘트가 없어요.</div>
        ) : (
          comments.map((c) => (
            <div key={c.id} style={{ display: 'flex', gap: 10 }}>
              <Avatar src={c.user.avatar} name={c.user.displayName} />
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{c.user.displayName}</span>
                  {c.isAdmin && (
                    <span
                      style={{
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 4,
                        background: 'var(--accent-soft)',
                        color: 'var(--accent)',
                        fontWeight: 700,
                      }}
                    >
                      관리자
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 14, color: 'var(--fg-soft)', marginTop: 2, whiteSpace: 'pre-wrap' }}>{c.body}</div>
              </div>
            </div>
          ))
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void post();
            }
          }}
          placeholder={isLoggedIn ? '코멘트 남기기…' : '로그인 후 코멘트'}
          style={{
            flex: '1 1 auto',
            background: 'var(--bg)',
            border: '1px solid var(--line)',
            borderRadius: 8,
            padding: '10px 12px',
            color: 'var(--fg)',
            fontSize: 14,
          }}
          maxLength={2000}
        />
        <button className="nf-btn accent" style={{ height: 40, padding: '0 16px' }} onClick={post} disabled={busy}>
          등록
        </button>
      </div>
    </ModalShell>
  );
}
